import React, { useState, useEffect } from "react";
import ScreenHeader from "../../../../../CommonComponents/ScreenHeader";
import { Form, Input, Divider } from "antd";
import "./CreatePoll.css";
import ChantButton from "../../../../../CommonComponents/ChantButton/ChantButton";
import ChantCheckbox from "../../../../../CommonComponents/ChantCheckbox/ChantCheckbox";
import ImageUploadButton from "../../../website/ImageUploaderButton";
import GroupSelector from "../../../../../CommonComponents/GroupSelector/GroupSelector";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import useUserStore, { selectUserDetails } from "../../../../../store/user/user";
import { db } from "../../../../../services/firebaseService/connection";
import { collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import {
  fbUploadPollImage,
  sendPollNotification,
} from "../../../../../services/firebaseService/endPoints/admin/content/polls";
import logger from "../../../../../utils/logger";
import useDashboardInfo from "../../../../../hooks/useDashboardInfo";
import useChildrenGroups from "../../../../clubAdmin/hooks/useChildrenGroups";

const CreatePoll = () => {
  const { pollId } = useParams();
  const isEditMode = !!pollId;

  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [pollImageFile, setPollImageFile] = useState("");
  const userData = useUserStore(selectUserDetails);
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const { isParent } = useDashboardInfo();

  const [options, setOptions] = useState([
    { id: 1, name: "", votes: 0 },
    { id: 2, name: "", votes: 0 },
  ]);
  const [existingPollData, setExistingPollData] = useState(null);

  // Group selector for clubadmin
  const { childGroupDetails } = useChildrenGroups();
  const [selectedGroups, setSelectedGroups] = useState({
    selectionType: "all",
    selectedValues: null,
    groupIds: null,
  });

  const handleGroupSelectionChange = (groupSelection) => {
    setSelectedGroups({
      selectionType: groupSelection.selectionType || "all",
      selectedValues: groupSelection.selectedValues || null,
      groupIds: groupSelection.groupIds || null,
    });
  };

  const fetchPollData = async () => {
    if (!pollId || !groupData) {
      return;
    }

    try {
      setFetchLoading(true);
      let pollDocRef;

      if (isParent) {
        pollDocRef = doc(db, "parentGroups", groupData.id, "polls", pollId);
      } else {
        pollDocRef = doc(db, "polls", groupData.id, "poll", pollId);
      }

      const pollDoc = await getDoc(pollDocRef);

      if (!pollDoc.exists()) {
        toast.error("Poll not found");
        navigate(isParent ? "/clubadmin/polls" : "/admin/content/polls");
        return;
      }

      const pollData = pollDoc.data();

      // Store existing poll data to preserve fields in edit mode
      setExistingPollData(pollData);

      // Set form values
      form.setFieldsValue({
        question: pollData.desc || "",
        provideFeedBack: pollData.provideFeedBack || false,
        showEntriesToMembers: pollData.showEntriesToMembers || false,
        showForFans: pollData.showForFans || false,
        hideResultsUntilClose: pollData.hideResultsUntilClose || false,
      });

      // Set poll image if exists
      if (pollData.bannerImage) {
        setPollImageFile(pollData.bannerImage);
      }

      // Set options from poll data
      const pollOptions = [];
      const optKeys = ["op1", "op2", "op3", "op4", "op5"];
      optKeys.forEach((key, index) => {
        if (pollData[key] && pollData[key] !== null) {
          pollOptions.push({
            id: index + 1,
            name: pollData[key].name || "",
            votes: pollData[key].votes || 0,
          });
        }
      });

      // Ensure at least 2 options
      if (pollOptions.length < 2) {
        while (pollOptions.length < 2) {
          pollOptions.push({
            id: pollOptions.length + 1,
            name: "",
            votes: 0,
          });
        }
      }

      setOptions(pollOptions);

      // Set poll duration - calculate remaining time from now to closesOn if poll is still active
      if (pollData.closesOn) {
        const closesOnTime = dayjs(pollData.closesOn * 1000);
        const now = dayjs();

        // If poll hasn't closed yet, show remaining time, otherwise show 0
        const duration = closesOnTime.isAfter(now) ? closesOnTime.diff(now) : 0;

        const days = Math.floor(duration / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (duration % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );

        form.setFieldsValue({
          pollDuration: {
            days: days > 0 ? days.toString() : "",
            hours: hours > 0 ? hours.toString() : "",
          },
        });
      }

      // Set selected groups for clubadmin
      if (isParent && pollData.audience) {
        setSelectedGroups({
          selectionType: pollData.audience.selectionType || "all",
          selectedValues: pollData.audience.selectedValues || null,
          groupIds: pollData.audience.groupIds || null,
        });
      }
    } catch (error) {
      logger.error("Error fetching poll data:", error);
      toast.error("Failed to load poll data");
      navigate(isParent ? "/clubadmin/polls" : "/admin/content/polls");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode && groupData) {
      fetchPollData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId, groupData]);

  const validateForm = (values) => {
    // Validate that first two options are filled
    if (!options[0].name || !options[1].name) {
      form.setFields([
        {
          name: "options",
          errors: ["The first two options are required"],
        },
      ]);
      return false;
    }

    // Update poll duration validation to check if both are missing
    const days = values?.pollDuration?.days;
    const hours = values?.pollDuration?.hours;

    if ((!days || days === "") && (!hours || hours === "")) {
      form.setFields([
        {
          name: ["pollDuration", "days"],
          errors: ["Please enter days or hours"],
        },
      ]);
      return false;
    }

    return true;
  };

  const handleRemoveOption = (selectedOption) => {
    const finalOptions = options
      .map((option) => {
        if (option.id > selectedOption.id) {
          return {
            ...option,
            id: option.id - 1,
          };
        }
        if (option.id < selectedOption.id) {
          return option;
        }
      })
      .filter((option) => option !== undefined);

    setOptions(finalOptions);
  };

  const handleOptionInput = (option, value) => {
    const optionsState = [...options];

    const selectedOptionIdx = optionsState.findIndex(
      (opt) => opt.id === option.id,
    );

    if (selectedOptionIdx > -1) {
      optionsState[selectedOptionIdx].name = value;
    }

    setOptions(optionsState);

    // Clear error if both required options are filled
    if (optionsState[0]?.name && optionsState[1]?.name) {
      form.setFields([
        {
          name: "options",
          errors: undefined,
        },
      ]);
    }
  };

  const handleImgUpload = async (pollImageFile) => {
    try {
      if (!pollImageFile) {
        return;
      }

      const downloadUrl = await fbUploadPollImage(
        pollImageFile,
        groupData?.groupName,
        Math.floor(dayjs().valueOf() / 1000).toString(),
      );

      return downloadUrl;
    } catch (err) {
      toast.error(
        "There was an error uploading image for the poll, could not complete the operation, contact admin",
      );
      return;
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitLoading(true);

      // Validate form
      if (!validateForm(values)) {
        setSubmitLoading(false);
        return;
      }

      // Handle image upload - only upload if it's a new file (not a URL string)
      let downloadUrl = "";
      if (pollImageFile) {
        // Check if pollImageFile is a URL (string starting with http) or a File object
        if (
          typeof pollImageFile === "string" &&
          pollImageFile.startsWith("http")
        ) {
          // It's already a URL, use it
          downloadUrl = pollImageFile;
        } else {
          // It's a new file, upload it
          downloadUrl = await handleImgUpload(pollImageFile);
        }
      }

      // Calculate closes on
      const now = dayjs();
      const futureTime = now
        .add(Number(values?.pollDuration?.days || 0), "day")
        .add(Number(values?.pollDuration?.hours || 0), "hour");
      const timestampInSeconds = Math.floor(futureTime.valueOf() / 1000);

      // Get options - preserve vote counts in edit mode
      const optionsObj = options.reduce(
        (acc, curr) => {
          const optionKey = `op${curr.id}`;
          // In edit mode, preserve votes from existing poll data if option name matches
          if (isEditMode && existingPollData) {
            const existingOption = existingPollData[optionKey];
            if (existingOption && existingOption.name === curr.name) {
              acc[optionKey] = {
                ...curr,
                votes: existingOption.votes || 0,
              };
            } else {
              acc[optionKey] = curr;
            }
          } else {
            acc[optionKey] = curr;
          }
          return acc;
        },
        { op1: null, op2: null, op3: null, op4: null, op5: null },
      );

      const body = {
        showEntriesToMembers: values?.showEntriesToMembers || false,
        provideFeedBack: values?.provideFeedBack || false,
        showForFans: values?.showForFans || false,
        hideResultsUntilClose: values?.hideResultsUntilClose || false,
        desc: values?.question,
        bannerImage: downloadUrl || "",
        closesOn: timestampInSeconds,
        ...optionsObj,
      };

      // For create mode, add additional fields
      if (!isEditMode) {
        body.isActive = true;
        body.isCancelled = false;
        body.noOfPolls = 0;
        body.createdByName = userData?.profile?.displayName;
        body.createdByDocId = userData?.profile?.uid;
        body.addedOn = Math.floor(dayjs().valueOf() / 1000);
      } else {
        // In edit mode, preserve existing fields that shouldn't be changed
        if (existingPollData) {
          body.isActive =
            existingPollData.isActive !== undefined
              ? existingPollData.isActive
              : true;
          body.isCancelled =
            existingPollData.isCancelled !== undefined
              ? existingPollData.isCancelled
              : false;
          body.noOfPolls =
            existingPollData.noOfPolls !== undefined
              ? existingPollData.noOfPolls
              : 0;
          body.createdByName =
            existingPollData.createdByName || userData?.profile?.displayName;
          body.createdByDocId =
            existingPollData.createdByDocId || userData?.profile?.uid;
          body.addedOn =
            existingPollData.addedOn || Math.floor(dayjs().valueOf() / 1000);
        }
      }

      // Add groups field if clubadmin
      if (isParent && selectedGroups.selectionType) {
        body.audience = {
          selectionType: selectedGroups.selectionType,
          selectedValues: selectedGroups.selectedValues,
          groupIds: selectedGroups.groupIds,
        };

        // Add groups array based on selection type
        if (selectedGroups.selectionType === "all") {
          body.groups = ["global"];
        } else if (selectedGroups.selectionType === "official") {
          body.groups = ["Official Groups"];
        } else {
          // For region or specific groups, use the groupIds
          body.groups = selectedGroups.groupIds || [];
        }
      }

      if (isEditMode) {
        // Update existing poll
        let pollDocRef;
        if (isParent) {
          pollDocRef = doc(db, "parentGroups", groupData.id, "polls", pollId);
        } else {
          pollDocRef = doc(db, "polls", groupData.id, "poll", pollId);
        }

        await updateDoc(pollDocRef, body);
        toast.success("Poll updated successfully!");
      } else {
        // Create new poll
        const pollsCollectionRef = isParent
          ? collection(db, "parentGroups", groupData.id, "polls")
          : collection(db, `polls/${groupData.id}/poll`);

        const docRef = await addDoc(pollsCollectionRef, body);
        const newPollId = docRef.id;

        // Only send notification for admin dashboard on create
        if (!isParent) {
          sendPollNotification(
            groupData?.id,
            groupData.groupName,
            newPollId,
            values?.question,
          );
        }
        toast.success("Poll created successfully!");
      }

      // Navigate back to polls list
      if (isParent) {
        navigate("/clubadmin/polls");
      } else {
        navigate("/admin/content/polls");
      }
    } catch (error) {
      logger.error("Error submitting form:", error);
      toast.error(
        isEditMode ? "Failed to update poll" : "Failed to create poll",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div style={{ padding: "12px" }}>
        <ScreenHeader title={isEditMode ? "Edit Poll" : "Create Poll"} />
        <div style={{ textAlign: "center", padding: "40px" }}>
          Loading poll data...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "12px" }}>
      <ScreenHeader title={isEditMode ? "Edit Poll" : "Create Poll"} />

      <div className="admin-poll-create-container">
        {isParent && (
          <div style={{ marginBottom: "20px" }}>
            <GroupSelector
              selectedGroups={selectedGroups}
              allGroups={childGroupDetails}
              onChange={handleGroupSelectionChange}
              typeLabel="Who should see this poll?"
            />
            <Divider style={{ margin: "16px 0" }} />
          </div>
        )}

        <div className="form-section">
          <Form
            form={form}
            layout="vertical"
            className="create-poll-form"
            disabled={submitLoading}
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <div>
              <Form.Item
                label={
                  <span className="form-label">
                    Question<span style={{ color: "#ff4d4f" }}> *</span>
                  </span>
                }
                name="question"
                rules={[{ required: true, message: "Please enter a question" }]}
              >
                <Input placeholder="Enter your question here" />
              </Form.Item>

              <Form.Item
                label={
                  <span className="form-label">Poll Image (Optional)</span>
                }
                name="image"
              >
                <ImageUploadButton
                  text="Upload"
                  images={pollImageFile ? [pollImageFile] : []}
                  onSave={(images) => {
                    setPollImageFile(images[0]);
                  }}
                />
              </Form.Item>

              <Form.Item
                label={<span className="form-label">Options</span>}
                name="options"
              >
                <div className="create-poll-options-container">
                  {options.map((option) => (
                    <div key={option.id}>
                      <p style={{ marginBottom: 0 }}>
                        {" "}
                        {`Option #${option.id}`}
                        {option.id <= 2 && (
                          <span style={{ color: "#ff4d4f" }}> *</span>
                        )}
                      </p>

                      <div className="create-poll-option">
                        <Input
                          value={option.name}
                          style={{ width: "40rem" }}
                          placeholder={`Option #${option.id}`}
                          onChange={(e) =>
                            handleOptionInput(option, e.target.value)
                          }
                        />
                        {option.id > 2 && (
                          <ChantButton
                            style={{ marginBottom: 0 }}
                            onClick={() => handleRemoveOption(option)}
                          >
                            ╳
                          </ChantButton>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {options.length < 5 && (
                  <ChantButton
                    style={{
                      marginTop: "12px",
                    }}
                    size="small"
                    onClick={() => {
                      setOptions((options) => [
                        ...options,
                        { id: options.length + 1, name: "", votes: 0 },
                      ]);
                    }}
                  >
                    + Add Option
                  </ChantButton>
                )}
              </Form.Item>

              <div style={{ paddingTop: "16px" }}>
                <Form.Item name="provideFeedBack" valuePropName="checked">
                  <ChantCheckbox>
                    Provide fields for members to enter additional feedback (max
                    150 characters)
                  </ChantCheckbox>
                </Form.Item>

                <Form.Item name="showEntriesToMembers" valuePropName="checked">
                  <ChantCheckbox>
                    Show individual responses to all members. If not selected,
                    only admin/leaders can see
                  </ChantCheckbox>
                </Form.Item>

                <Form.Item name="hideResultsUntilClose" valuePropName="checked">
                  <ChantCheckbox>
                    Hide results from members until poll closes.
                  </ChantCheckbox>
                </Form.Item>

                {groupData?.regCodeRole === "fan" && (
                  <Form.Item name="showForFans" valuePropName="checked">
                    <ChantCheckbox>
                      Allow fans to see poll and vote?
                    </ChantCheckbox>
                  </Form.Item>
                )}
              </div>

              <Form.Item
                label={
                  <span className="form-label">
                    Poll Duration
                    <span style={{ color: "#ff4d4f" }}> *</span>
                  </span>
                }
              >
                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  <Form.Item
                    name={["pollDuration", "days"]}
                    noStyle
                    dependencies={[["pollDuration", "hours"]]}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const hours = getFieldValue([
                            "pollDuration",
                            "hours",
                          ]);
                          if (
                            (!value || value === "") &&
                            (!hours || hours === "")
                          ) {
                            return Promise.reject("Please enter days or hours");
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input
                      type="number"
                      min={0}
                      style={{ width: "140px" }}
                      placeholder="Days"
                      addonAfter="days"
                      onWheel={(e) => e.target.blur()}
                    />
                  </Form.Item>
                  <Form.Item name={["pollDuration", "hours"]} noStyle>
                    <Input
                      type="number"
                      min={0}
                      style={{ width: "140px" }}
                      placeholder="Hours"
                      addonAfter="hours"
                      onWheel={(e) => e.target.blur()}
                    />
                  </Form.Item>
                </div>
              </Form.Item>

              <div className="flex-center" style={{ width: "100%" }}>
                <Form.Item>
                  <ChantButton
                    type="primary"
                    htmlType="submit"
                    loading={submitLoading}
                    style={{ marginTop: "24px" }}
                  >
                    {isEditMode ? "Update Poll" : "Publish Poll"}
                  </ChantButton>
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default CreatePoll;
